import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { tenantApiFetch } from './tenantApi';
import { TenantCan } from '../components/tenant/TenantCan';

function statusColor(s) {
  if (s === 'active') return 'success';
  if (s === 'archived') return 'default';
  return 'warning';
}

export default function TenantRoleDetailPage() {
  const { roleId } = useParams();
  const navigate = useNavigate();
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await tenantApiFetch(`/api/tenant/roles/${roleId}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Not found');
      setRole(json.role);
    } catch (e) {
      setError(e.message);
      setRole(null);
    } finally {
      setLoading(false);
    }
  }, [roleId]);

  useEffect(() => {
    load();
  }, [load]);

  const doDelete = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await tenantApiFetch(`/api/tenant/roles/${roleId}`, { method: 'DELETE' });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Delete failed');
      navigate('/tenant/roles');
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
      setConfirmOpen(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !role) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!role) return null;

  const row = (label, value) => (
    <Grid item xs={12} sm={6}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1">{value ?? '—'}</Typography>
    </Grid>
  );

  const isSystem = role.is_system_role || role.is_system;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography variant="h4">{role.role_name || role.name}</Typography>
          <Typography variant="body2" color="text.secondary">
            <code>{role.role_code || role.slug}</code>
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Button variant="outlined" onClick={() => navigate('/tenant/roles')}>
            Back to list
          </Button>
          <Button variant="outlined" onClick={() => navigate(`/tenant/roles/${roleId}/permissions`)}>
            Permission matrix
          </Button>
          <TenantCan permission="tenant.roles.manage">
            <Button variant="contained" onClick={() => navigate(`/tenant/roles/${roleId}/edit`)}>
              Edit
            </Button>
            {!isSystem && (
              <Button color="error" variant="outlined" onClick={() => setConfirmOpen(true)}>
                Delete
              </Button>
            )}
          </TenantCan>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Chip label={role.status} color={statusColor(role.status)} />
          {isSystem ? <Chip label="System role" /> : <Chip label="Custom" variant="outlined" />}
        </Box>
        <Grid container spacing={2}>
          {row('Description', role.description)}
          {row('Users assigned', role.user_count != null ? String(role.user_count) : '—')}
          {row('Created', role.created_at ? String(role.created_at) : '—')}
          {row('Updated', role.updated_at ? String(role.updated_at) : '—')}
        </Grid>
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle1" gutterBottom>
          Permissions
        </Typography>
        {isSystem && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            System role permission set is fixed in this product build; only the display name and description can be
            edited.
          </Typography>
        )}
        <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-wrap' }}>
          {(role.permissions || []).length ? (role.permissions || []).join('\n') : '—'}
        </Typography>
      </Paper>

      <Dialog open={confirmOpen} onClose={() => !busy && setConfirmOpen(false)}>
        <DialogTitle>Delete role?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This cannot be undone. Users must be unassigned first.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={busy}>
            Cancel
          </Button>
          <Button color="error" onClick={doDelete} disabled={busy}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
